import * as moment from "moment";
import * as angular from "angular";
import * as URI from "urijs";
import * as d3 from "d3";
import * as Models from "./rlAdmin.models";
import * as Utils from "./rlAdmin.utils";
import * as Consts from "./rlAdmin.consts";
import * as Filters from "./rlAdmin.filters";

import { EntityService } from "../services/entity-service";
import { NotifyService } from "../services/notify-service";
import { LocalCacheService } from "../services/local-cache-service";
import { SearchService } from "../services/search-service";
import { BaseSearchController } from "../controllers/_baseSearchController";
import { EventService } from "../services/event-service";
import { ContactService } from "../services/contact-service";
import { ReportService } from "../services/report-service";
import { AclService } from "../services/acl-service";
import { BaseEntityController } from "../controllers/baseEntityController";
import { FavoriteService } from "../services/favorite-service";
import { EntityConfigService } from "../services/entity-config-service";
import { RoyaltyService } from "../services/royalty-service";
import { DateLocaleService } from "../services/date-locale-service";
import { AccountService } from "../services/account-service";
import { SecurityService } from "../services/security-service";
import { AdminService } from "../services/admin-service";
import { RightsFilterService } from "../services/rights-filter-service";
import { JsonUtil } from '../ts/rlAdmin.utils';

/*
 * Controller to handle the outer site page layout
 * takes side panels open state into account
 */
export class LayoutController {

    static $inject = ["$scope", "$timeout", "storage"];

    layoutState: Models.ILayoutState = null;
    layoutCss: string = "";

    private key = "layoutStateV2";

    constructor($scope: ng.IScope, $timeout: ng.ITimeoutService) {
        this.layoutState = JSON.parse(window.localStorage.getItem(this.key));

        if (this.layoutState == null) {
            this.layoutState = {
                compactMenu: false,
                isSidePanelOpen: true
            };
        }

        // whenever the layoutstate changes, update layout css
        $scope.$watch(
            () => this.layoutState,
            (newValue: Models.ILayoutState) => {
                var state = newValue;
                if (state.compactMenu) {
                    this.layoutCss = "compact-menu";
                } else {
                    this.layoutCss = "";
                }
                // store state in cookie for next load
                window.localStorage.setItem(this.key, JSON.stringify(state));
            }, true);
    }

    Toggle() {
        this.layoutState.compactMenu = !this.layoutState.compactMenu;
    }

    ToggleSidePanel() {
        this.layoutState.isSidePanelOpen = !this.layoutState.isSidePanelOpen;
    }
}

export class RecordCreateController {
    static $inject = ["$scope", "$window", "$timeout", "entityService", "entityConfigService", "notifyService", "aclService"];

    divisionID: number;
    charTypeID: number;
    templateID: number;
    successUrl: string;
    cancelUrl: string;
    editMode: boolean;
    visibility: number;
    saving: boolean;
    autoCreateChildren: boolean;
    hasChildTemplates: boolean;
    entityCharDataCollection: Models.ICharDataCollection;

    isNewAssociation: boolean;
    associateToParentEntityID: string;
    isParentAssociation: boolean;

    viewNewRecordOnCreate: boolean;

    canCreate: boolean = false;
    canEdit: boolean = false;

    constructor(private $scope: ng.IScope, private $window: ng.IWindowService, private $timeout: ng.ITimeoutService,
        private entityService: EntityService, private entityConfigService: EntityConfigService,
        private notifyService: NotifyService, private aclService: AclService) {
        this.divisionID = 0;
        this.charTypeID = -1;
        this.templateID = -1;
        this.successUrl = "";
        this.cancelUrl = "";
        this.editMode = true;
        this.visibility = 1;
        this.entityCharDataCollection = null;
        this.saving = false;
        this.viewNewRecordOnCreate = true;
        this.autoCreateChildren = false;
        this.hasChildTemplates = false;

        $scope.$watch(() => this.templateID, (newValue, oldValue) => {
            if (newValue != oldValue) {
                this.GetTemplate();
            }
        });

    }

    Init(charTypeID: number, templateID: number, successUrl: string, cancelUrl: string, templateGroupIDs: number[], isNewAssociation?: boolean, associateToParentEntityID?: string, isParentAssociation?: boolean) {
        this.charTypeID = charTypeID;
        this.successUrl = successUrl;
        this.cancelUrl = cancelUrl;
        this.templateID = templateID;

        this.isNewAssociation = isNewAssociation;
        this.associateToParentEntityID = associateToParentEntityID;
        this.isParentAssociation = isParentAssociation;

        if (this.isNewAssociation) {
            this.viewNewRecordOnCreate = false;
        }

        //Get ACLs for user
        var templateEntityID = Utils.IdBuilder.buildTemplateID(this.divisionID, this.charTypeID, this.templateID);
        this.entityService.getEmptyEntityCharData(templateEntityID)
            .then((result) => {
                var targetACL = result.templateMetaData.template.acl;
                this.canCreate = Utils.AclUtil.hasCreateAccess(this.aclService.acls, targetACL);
                this.canEdit = Utils.AclUtil.hasWriteAccess(this.aclService.acls, targetACL);
            });

        this.GetTemplate();
    }

    GetTemplate() {
        if (this.templateID > 0) {
            this.notifyService.startProgress();
            var templateID = Utils.IdBuilder.buildTemplateID(this.divisionID, this.charTypeID, this.templateID);
            var parentCharTypeID;

            if (!_.isEmpty(this.associateToParentEntityID)) {
                parentCharTypeID = Utils.IdBuilder.splitEntityID(this.associateToParentEntityID).charTypeID;
            }
            else {
                parentCharTypeID = 0;
            }

            this.entityService.getAssociatedTemplatesForTemplate(this.charTypeID, this.templateID, this.charTypeID).
                then((result) => {
                    if (!this.isParentAssociation || parentCharTypeID != Consts.CharTypeIDs.property) {
                        if (result.length > 0) {
                            this.hasChildTemplates = true;
                        }
                    }
                });
            this.entityService.getEmptyEntityCharData(templateID)
                .then((result) => {
                    this.divisionID = result.templateMetaData.divID;
                    this.entityCharDataCollection = result;
                })
                .finally(() => {
                    this.notifyService.endProgress();
                });

        }
    }

    SaveEntity() {
        if (!this.saving) {
            this.saving = true;
            this.notifyService.startProgress();
            var templateID = Utils.IdBuilder.buildTemplateID(this.divisionID, this.charTypeID, this.templateID);

            var parentEntityID = this.associateToParentEntityID;

            if (parentEntityID == "-1") {
                //There is no parent entity in which to associate to, set this value as a min entityID
                parentEntityID = null;
            }

            return this.entityService.createEntity(templateID, parentEntityID, this.entityCharDataCollection.charDatas, true)
                .then((result) => {
                    if (result.success) {
                        //If the user does not have Write access, then redirect them to the newly created entity, NOT the Contacts Association page
                        if (!this.canEdit) {
                            var entityUrl = "/Go/" + result.entityID;
                            this.editMode = false;
                            this.$timeout(() => {
                                this.$window.location.assign(entityUrl);
                            });
                        }
                        else {
                            var recID = Utils.IdBuilder.splitEntityID(result.entityID).recID;
                            var successUrl = this.successUrl.replace("-1", recID.toString());

                            if (this.charTypeID == Consts.CharTypeIDs.property && this.autoCreateChildren) {
                                successUrl = successUrl += "&chy=1";
                            }

                            if (this.isNewAssociation) {
                                successUrl = successUrl.replace("-2", this.associateToParentEntityID);
                                //Associate the deal to the parent entity id
                                this.DoAssociation(result.entityID);
                                successUrl += "&an=1";

                                if (this.viewNewRecordOnCreate) {
                                    successUrl = successUrl + "&v=1";
                                }
                            }
                            else if (!this.isNewAssociation && this.associateToParentEntityID != null && this.associateToParentEntityID != "-1") {
                                if (this.viewNewRecordOnCreate) {
                                    successUrl = successUrl.replace("-2", this.associateToParentEntityID) + "&v=1";
                                }
                                else {
                                    successUrl = successUrl.replace("-2", this.associateToParentEntityID);
                                }

                                this.CopyParentRelationships(result.entityID);
                            }

                            this.editMode = false;
                            this.$timeout(() => {
                                this.$window.location.assign(successUrl);
                            });
                        }
                    } else {
                        this.notifyService.notifyError("There was an error creating your entity.");
                    }
                }, () => {
                    this.saving = false;
                    this.notifyService.notifyError("There was an error creating your entity.");
                })
                .finally(() => {
                    this.notifyService.endProgress();
                });
        }
    }

    CopyParentRelationships(newEntityID: string) {
        var direction = this.isParentAssociation ? 1 : 0;

        if (direction === 0) {
            //Copy all valid child relationships from the parent to the to be created child
            this.entityService.getEntityRelationships(this.associateToParentEntityID)
                .then((results) => {

                    var parentEntityID = Utils.IdBuilder.splitEntityID(this.associateToParentEntityID);

                    //We're only interested in child relationships of the original parentEntity
                    var validRels = _.filter(results, r => r.childRecID != parentEntityID.recID);

                    var childEntityIDs: string[] = [];
                    _.forEach(validRels, (validRel) => {
                        var newEntityID = Utils.IdBuilder.buildEntityID(validRel.divisionID, validRel.childCharTypeID, validRel.childRecID);

                        childEntityIDs.push(newEntityID);
                    });
                    if (childEntityIDs.length > 0) {
                        this.entityService.associateEntity(newEntityID, childEntityIDs);
                    }
                })
                .finally(() => {
                    var childEntityIDs: string[] = [];
                    childEntityIDs.push(this.associateToParentEntityID);

                    this.entityService.associateEntity(newEntityID, childEntityIDs);
                });
        }
        else {
            //Just associate the "parent"/initiating entity to the newly created entity
            var childEntityIDs: string[] = [];
            childEntityIDs.push(this.associateToParentEntityID);

            this.entityService.associateEntity(newEntityID, childEntityIDs);
        }
    }

    DoAssociation(newEntityID: string) {
        this.notifyService.startProgress();

        var childEntityIDs: string[] = [];

        childEntityIDs.push(newEntityID);

        var direction = this.isParentAssociation ? 1 : 0;

        this.entityService.associateEntity(this.associateToParentEntityID, childEntityIDs, direction)
            .then((result) => {
                this.notifyService.endProgress();
            });
    }

    Cancel() {
        if (this.isNewAssociation) {
            var recID = Utils.IdBuilder.splitEntityID(this.associateToParentEntityID).recID;
            var cancelUrl = this.cancelUrl.replace("-1", recID.toString());
            this.$window.location.assign(cancelUrl);
        }
        else {
            this.$window.location.assign(this.cancelUrl);
        }

    }

    Save(isValid) {
        if (!isValid) {
            this.notifyService.notifyError("Correct Errors");
        } else {
            return this.SaveEntity();
        }
    }
}

export class DashboardMyNewDealsController extends BaseSearchController<Models.IEntitySearchDoc>
{
    static $inject = ["$scope", "$location", "$filter", "$window",
        "localCacheService", "notifyService", "entityService", "$q", "searchService"];

    templateGroupIDs: number[];
    overridePage: number;

    constructor(
        $scope: ng.IScope,
        $location: ng.ILocationService,
        private $filter: ng.IFilterService,
        private $window: ng.IWindowService,
        localStorage: LocalCacheService,
        notifyService: NotifyService,
        entityService: EntityService,
        $q: ng.IQService,
        searchService: SearchService) {

        super($scope, $q, $location, $filter, localStorage, notifyService, entityService, searchService);

        this.overridePage = 1;
        this.templateGroupIDs = [];
        this.rowCount = 10;

        $scope.$watch(() => [this.rowCount, this.overridePage], (newValues, oldValues) => {
            if (newValues[0] != oldValues[0] || newValues[1] != oldValues[1]) {
                this.GetMyNewDeals();
            }
        });
    }

    Init() {
        var promise = this.GetMyNewDeals();
        this.$q.all([promise]);
        this.initialized = true;
    }

    Search() { }

    // calls a search using	current	state data
    GetMyNewDeals(): ng.IPromise<any> {
        // build up	search request
        var searchState: Models.IEntitySearchRequest = {
            k: this.keywords,			// keywords
            t: this.selectedTemplate,	// selected	template
            s: ["DNR Created|1", "DNR Created|7", "DNR Submitted|3", "DNR Submitted|9", "DNR Submitted|14", "Test Option Created|10", "Term Deal DNR Created|1", "Term Deal DNR Submitted|2"],   // hard coded to what sony is defining as Open for div 15 HACK ALERT
            p: this.overridePage,				// current page
            r: this.rowCount,					// row count
            ct: Consts.CharTypeIDs.transaction, //hard coded to deals
            sf: "created",		// sort Field
            sd: 1,			// sort direction 0 - ascending, 1 - descending
            tgs: this.templateGroupIDs,  //templateGroupIDs number[]
            rtm: true
        };

        // call	search service
        return this.entityService.search(searchState)
            .then((result) => {
                this.itemRows = result.resultDocs;
                this.qTime = result.qTime;
                this.numFound = result.numFound;
                this.pageCount = result.pageCount;
                this.jumpTo = this.page;
                this.templates = result.templateFacets;
            });
    }

}

export class DashboardMyOpenDealsController extends BaseSearchController<Models.IEntitySearchDoc>
{
    static $inject = ["$scope", "$location", "$filter", "$window",
        "localCacheService", "notifyService", "entityService", "$q", "searchService"];

    templateGroupIDs: number[];
    overridePage: number;

    constructor(
        $scope: ng.IScope,
        $location: ng.ILocationService,
        private $filter: ng.IFilterService,
        private $window: ng.IWindowService,
        localStorage: LocalCacheService,
        notifyService: NotifyService,
        entityService: EntityService,
        $q: ng.IQService,
        searchService: SearchService) {

        super($scope, $q, $location, $filter, localStorage, notifyService, entityService, searchService);

        this.templateGroupIDs = [];
        this.overridePage = 1;
        this.rowCount = 10;

        $scope.$watch(() => [this.rowCount, this.overridePage], (newValues, oldValues) => {
            if (newValues[0] != oldValues[0] || newValues[1] != oldValues[1]) {
                this.GetMyOpenDeals();
            }
        });
    }

    Init() {
        var promise = this.GetMyOpenDeals();
        this.$q.all([promise]);
        this.initialized = true;
    }

    Search() { }

    // calls a search using	current	state data
    GetMyOpenDeals(): ng.IPromise<any> {
        // build up	search request
        var searchState: Models.IEntitySearchRequest = {
            k: this.keywords,			                // keywords
            t: this.selectedTemplate,	                // selected	template
            s: ["Seeking Presidents' Approval|4",
                "Seeking Presidents' Approval|11",
                "Seeking BA Executive Approval|5",
                "Seeking BA Executive Approval|12",
                "Seeking Finance Approval|6",
                "Seeking Finance Approval|10",
                "In Negotiation|2",
                "In Negotiation|3",
                "In Negotiation|5",
                "In Negotiation|7",
                "In Negotiation|8",
                "Term Deal Fee Pending|12",
                "Term Deal Fee to be Verified|13"],     // hard coded to what sony is defining as Open for div 15 HACK ALERT
            p: this.overridePage,				        // current page
            r: this.rowCount,					                    // row count
            ct: Consts.CharTypeIDs.transaction,         // hard coded to deals
            sf: "created",		                        // sort Field
            sd: 1,			                            // sort direction 0 - ascending, 1 - descending
            tgs: this.templateGroupIDs,                 // templateGroupIDs number[]
            rtm: true
        };

        // call	search service
        return this.entityService.search(searchState)
            .then((result) => {
                this.itemRows = result.resultDocs;
                this.qTime = result.qTime;
                this.numFound = result.numFound;
                this.pageCount = result.pageCount;
                this.jumpTo = this.page;
                this.templates = result.templateFacets;
            });
    }
}




export class TestEntityController {

    static $inject = ["$window", "entityService"];

    entityId: string;
    entityData: Models.ICharDataCollection;
    editMode: boolean;
    visibility: number;

    constructor(private $window: ng.IWindowService, private entityService: EntityService) {
        this.entityId = "";
        this.entityData = null;
        this.editMode = true;
        this.visibility = 1;


    }

    Init(entityID: string) {
        this.entityId = entityID;
        this.LoadCharData();
    }

    private isEntity(entityID: string) {
        return entityID.match(/^d(\d+)c(\d+)r(\d+)$/)
    }

    private isTemplate(entityID: string) {
        return entityID.match(/^d(\d+)c(\d+)t(\d+)$/)
    }

    LoadCharData() {
        if (this.isEntity(this.entityId)) {
            this.entityService.getEntityCharData(this.entityId)
                .then((result) => {
                    this.entityData = result;
                });
        } else if (this.isTemplate(this.entityId)) {
            this.entityService.getEmptyEntityCharData(this.entityId)
                .then((result) => {
                    this.entityData = result;
                });
        }
    }

    ShowData() {
        this.$window.alert(JSON.stringify(this.entityData.charDatas));
    }

    SaveData() {
        if (this.isEntity(this.entityId)) {
            this.entityService.saveCharData(this.entityId, this.entityData.charDatas);
        } else {
            this.entityService.createEntity(this.entityId, null, this.entityData.charDatas)
                .then((result) => {
                    if (result.success) {
                        this.$window.location.assign("/entity/test/" + result.entityID);
                    } else {
                        this.$window.alert("error");
                    }
                });
        }
    }
}

export class RecordDetailController {
    static $inject = ["$scope", "entityService", "notifyService", "eventService", "contactService", "entityConfigService", "$window"];

    entityID: string;
    entityRecord: Models.IEntity;
    entityCharDataCollection: Models.ICharDataCollection;
    canEdit: boolean;
    editMode: boolean;
    visibility: number;
    isValid: boolean;
    charTypeName: string;
    listUrl: string;
    locked: boolean;
    selectedEntity: any;
    initialized: boolean;
    hasChildTemplates: boolean;
    jobTitle: string;
    jobID: string;
    showJobProgressStatus: boolean;
    progressStatus: string;

    catalogHierarchy: Models.ICatalogHierarchy[];
    catalogHierarchyIDTitles: { [recTitle: string]: number } = {};
    catalogRecordHierarchy: { [recID: number]: Models.ICatalogHierarchy[] } = {};
    catalogRecTopMostParents: { [recID: number]: string[] } = {};
    catalogRecDirectParents: { [recID: number]: string[] } = {};
    catalogRecHierarchyPathKeys: number[] = [];
    catalogRecHierarchyPaths: { [recID: number]: string[] } = {};
    showCharsToSync: boolean;
    selectedSyncOption: number;
    charTypeID: number;
    templateID: number;
    showSyncSelectOption: boolean;
    showSelectCharacteristicForSync: boolean;
    selectedSyncCharacteristics: Models.ICharacteristicMetaData[] = [];
    hideCharsForSync: boolean = false;

    constructor(private $scope: ng.IScope, private entityService: EntityService, private notifyService: NotifyService,
        private eventService: EventService, private contactService: ContactService, private entityConfigService: EntityConfigService,
        private $window: ng.IWindowService) {
        this.entityID = "";
        this.entityRecord = null;
        this.entityCharDataCollection = null;
        this.editMode = false;
        this.visibility = 1;
        this.canEdit = false;
        this.isValid = false;
        this.charTypeName = "";
        this.listUrl = "~/";
        this.locked = false;
        this.initialized = false;
        this.hasChildTemplates = false;
        this.showJobProgressStatus = false;
        this.jobID = "";
        this.jobTitle = "";
        this.progressStatus = "";
        this.catalogHierarchy = [];

        $scope.$watch(() => {
            if (this.entityID == null) {
                return null;
            } else {
                return this.entityID;
            }
        },
            (newValue, oldValue) => {
                if (newValue != null) {
                    this.eventService.on(Consts.Events.WorkflowExecuted + newValue, () => {
                        return this.LoadEntity();
                    });
                }
            });

        this.$scope.$on(Consts.Events.ViewEntity, (e, data) => {
            if (!_.isEmpty(data)) {
                this.ViewEntity(data);
            }
        });

        $scope.$watch(() => this.progressStatus, (newValue, oldValue) => {
            if (newValue == "Done") {
                this.showJobProgressStatus = false;
                this.LoadEntity();
            }
        });
    }

    Init(entityID: string, canEdit: string, charTypeName: string, listUrl: string, editMode?: string) {
        this.entityID = entityID;
        this.hasChildTemplates = false;
        if (!_.isEmpty(canEdit)) {
            this.canEdit = canEdit.toLowerCase() == "true";
        }
        else {
            this.canEdit = false;
        }

        this.LoadEntity()
            .then(() => {
                this.charTypeID = this.entityRecord.charTypeID;
                this.templateID = this.entityRecord.templateID;

                if (this.entityRecord.charTypeID == Consts.CharTypeIDs.property) {
                    this.hideCharsForSync = true;
                    this.catalogHierarchy = [];
                    this.catalogHierarchyIDTitles = {};
                    this.catalogRecordHierarchy = {};
                    this.catalogRecDirectParents = {};
                    this.catalogRecTopMostParents = {};
                    this.catalogRecHierarchyPaths = {};

                    this.entityConfigService.getCatalogHierarchy(this.entityRecord.recordID.toString())
                        .then((result) => {
                            if (result.success) {
                                this.catalogHierarchy = result.data;

                                var hierarchyPaths = this.catalogHierarchy.map(h => h.hierarchyPath).filter(function (e, i, a) {
                                    return i === a.indexOf(e);
                                });

                                this.catalogRecHierarchyPaths[this.entityRecord.recordID] = hierarchyPaths;

                                _.forEach(this.catalogHierarchy, (ch) => {
                                    if (!this.catalogHierarchyIDTitles[ch.directParentTitle]) {
                                        this.catalogHierarchyIDTitles[ch.directParentTitle] = ch.directParentRecID;
                                    }

                                    if (!this.catalogHierarchyIDTitles[ch.topMostParentTitle]) {
                                        this.catalogHierarchyIDTitles[ch.topMostParentTitle] = ch.topMostParentRecID;
                                    }
                                });
                            }
                        })
                        .finally(() => {
                            this.initialized = true;
                        });
                }
                else {
                    this.initialized = true;
                }
            });

        if (!_.isEmpty(charTypeName)) {
            this.charTypeName = charTypeName;
        }

        if (!_.isEmpty(listUrl)) {
            this.listUrl = listUrl;
        }

        if (editMode) {
            this.editMode = editMode.toLowerCase() == "true";
            this.isValid = !this.editMode;// if overriding to be in edit mode, record must be invalid
        }
    }

    LoadEntity(): ng.IPromise<any> {
        this.notifyService.startProgress();
        return this.entityService.getEntity(this.entityID)
            .then((result) => {
                this.entityRecord = result.record;
                this.entityCharDataCollection = result.charDataCollection;

                if (result.charDataCollection != null) {
                    if (result.charDataCollection.status.lockIndicator == 0) {
                        this.locked = false;
                    }
                    else {
                        this.locked = true;
                    }

                    if (result.record.charTypeID == Consts.CharTypeIDs.property && this.canEdit) {
                        this.entityService.getAssociatedTemplatesForTemplate(Consts.CharTypeIDs.property, result.record.templateID, Consts.CharTypeIDs.property).
                            then((result) => {
                                if (result.length > 0) {
                                    this.hasChildTemplates = true;
                                }
                            });
                    }
                }
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    SaveEntity() {
        this.notifyService.startProgress();
        return this.entityService.saveCharData(this.entityID, this.entityCharDataCollection.charDatas)
            .then(() => { this.notifyService.notifySuccess("Successfully Saved"); }, this.notifyService.errorResponseHandler)
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    Edit() {
        this.editMode = true;
    }

    Cancel() {
        this.notifyService.startProgress();
        this.showCharsToSync = false;
        this.selectedSyncOption = 0;
        this.LoadEntity()
            .then(() => {
                this.editMode = false;
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    Save(isValid: boolean) {
        if (!isValid) {
            this.notifyService.notifyError("Correct Errors");
        } else {
            return this.SaveEntity()
                .then(() => {
                    this.LoadEntity()
                        .then(() => {
                            this.RefreshValidation();
                            this.editMode = false;
                        });
                });
        }
    }

    Delete() {

        if (this.$window.confirm("Are you sure you want to delete this " + this.charTypeName + "?") && this.$window.confirm("Are you REALLY SURE you want to delete this " + this.charTypeName + "?")) {
            this.notifyService.startProgress();
            this.entityService.deleteEntity(this.entityRecord.id)
                .then(() => {
                    this.$window.location.assign(this.listUrl);
                },
                    () => {
                        this.notifyService.notifyError("Error Deleting " + this.charTypeName);
                    })
                .finally(() => {
                    this.notifyService.endProgress();
                });
        } else {
            this.$scope.$broadcast(Consts.Events.NotifyDone);
        }
    }

    RefreshAll(): ng.IPromise<any> {
        return this.RefreshValidation();
    }

    RefreshValidation(): ng.IPromise<any> {
        return this.eventService.call(Consts.Events.ValidateEntity);
    }

    CopySuccessful(newEntityID: string) {
        //copy succeeded, navigate user to new entity
        this.$window.location.assign("/Go/" + newEntityID);
    }

    ViewEntity(viewEntityID: string) {
        this.entityService.getEntity(viewEntityID)
            .then((res) => {
                this.selectedEntity = res;
            });
    }

    ShowSyncRecordMetaDataToHierarchicalRecordsModal() {
        this.showCharsToSync = true;
        this.showSyncSelectOption = true;
    }

    public SelectSyncOption() {
        if (this.selectedSyncOption == 2) {
            this.showSelectCharacteristicForSync = true;
            this.showSyncSelectOption = false;
        }
        else {
            this.showSelectCharacteristicForSync = false;
            this.showSyncSelectOption = false;
            this.selectedSyncCharacteristics = [];
            this.SyncSelectedOptionToHierarchicalRecords();
        }
    }

    public CharacteristicsSelected(characteristics: Models.ICharacteristicMetaData[]) {
        this.showSyncSelectOption = false;
        this.showSelectCharacteristicForSync = false;
        this.selectedSyncCharacteristics = characteristics;
        this.SyncSelectedOptionToHierarchicalRecords();
    }

    private SyncSelectedOptionToHierarchicalRecords() {
        if (this.$window.confirm("Are you sure you want to sync the values in matching characteristics down this Catalog hierarchy?")) {
            this.showCharsToSync = false;
            var syncMode: number;
            if (this.selectedSyncOption == 2) {
                syncMode = Consts.SyncMode.SyncCharacteristics;
            }
            else {
                syncMode = Consts.SyncMode.SyncAllCharacteristics;
            }
            this.selectedSyncOption = 0;
            var charIDs = this.selectedSyncCharacteristics.map(chr => chr.characteristicID);

            var charTypeID = Utils.IdBuilder.splitEntityID(this.entityID).charTypeID;
            if (charTypeID == Consts.CharTypeIDs.property && this.hasChildTemplates && this.canEdit) {
                var recID = Utils.IdBuilder.splitEntityID(this.entityID).recID;
                var request: Models.ISyncMetaDataOrPartiesToHierarchicalRecordsRequest = {
                    charTypeID: charTypeID,
                    recID: recID,
                    partyID: -1,
                    contactID: -1,
                    characteristicIDs: charIDs,
                    syncMode: syncMode
                };

                this.notifyService.startProgress();
                this.entityService.syncMetaDataOrPartiesToHierarchicalRecords(request)
                    .then((result) => {
                        this.showJobProgressStatus = true;
                        this.jobID = result.jobID;
                        this.jobTitle = result.jobTitle;
                    })
                    .finally(() => {
                        this.notifyService.endProgress();
                    });
            }
        }
    }
}

/* controller for availability page */
export class AvailabilityReportController extends BaseEntityController {
    static $inject = ["$scope", "$q", "$window", "reportService", "localCacheService", "notifyService", "entityService", "aclService", "eventService"];
    entityID: string;
    startDate: string;
    endDate: string;
    mode: number;
    selectedTerritoryIds: number[];
    selectedMediaIds: number[];
    selectedTerritories: string[];
    selectedMedias: string[];
    nodes: Models.IPropertyAvailability[];
    canCopy: boolean;
    locked: boolean;
    initialized: boolean;

    constructor($scope: ng.IScope, $q: ng.IQService, private $window: ng.IWindowService, private reportService: ReportService,
        localStorage: LocalCacheService, notifyService: NotifyService, entityService: EntityService, private aclService: AclService, private eventService: EventService) {

        super($scope, $q, entityService, localStorage, notifyService);

        var now = moment();
        this.entityID = "";
        this.startDate = now.format("L");
        this.endDate = now.add("days", 30).format("L");
        this.mode = 0;
        this.selectedTerritoryIds = [];
        this.selectedMediaIds = [];
        this.selectedTerritories = [];
        this.selectedMedias = [];
        this.nodes = [];
        this.locked = false;
        this.canCopy = false;
        this.initialized = false;

        $scope.$watch(() => {
            if (this.entityID == null) {
                return null;
            } else {
                return this.entityID;
            }
        },
            (newValue, oldValue) => {
                if (newValue != null) {
                    this.eventService.on(Consts.Events.WorkflowExecuted + newValue, () => {
                        this.RefreshParentRecordStatus();
                    });
                }
            });
    }

    Init() {
        var parentID = -1;
        this.reportService.getRows(parentID, "", "").then((result) => {
            this.nodes = result;
        });
    }

    GetAvailableRights(entityID: string, parentID: number) {
        this.entityID = entityID;
        this.aclService.loadAcls()
            .then((acls) => {
                this.canCopy = Utils.AclUtil.hasCreateAccess(acls, Consts.Acl.Activities._Site.Mod1);
            });

        this.entityService.getEntity(entityID)
            .then((result) => {
                this.entity = result.record;
                if (result.charDataCollection.status.lockIndicator > 0) {
                    this.locked = true;
                }
                else {
                    this.locked = false;
                }
                this.initialized = true;
                this.Validate();
            });

        this.reportService.getPropertyAvailsRows(entityID, parentID, "", "")
            .then((result) => {
                this.nodes = result;
            });
    }

    CopySuccessful(newEntityID: string) {
        //copy succeeded, navigate user to new entity
        this.$window.location.assign("/Go/" + newEntityID);
    }

    Delete() {
        if (this.$window.confirm("Are you sure you want to delete this Catalog Item?") && this.$window.confirm("Are you REALLY SURE you want to delete this Catalog Item?")) {
            this.notifyService.startProgress();
            this.entityService.deleteEntity(this.entityID)
                .then(() => {
                    this.$window.location.assign(Consts.Urls.PropertyListPage);
                },
                    () => {
                        this.notifyService.notifyError("Error Deleting Project");
                    })
                .finally(() => {
                    this.notifyService.endProgress();
                });
        } else {
            this.$scope.$broadcast(Consts.Events.NotifyDone);
        }
    }

    RefreshParentRecordStatus() {
        this.entityService.getEntity(this.entityID)
            .then((result) => {
                this.entity = result.record;
                if (result.charDataCollection.status.lockIndicator > 0) {
                    this.locked = true;
                }
                else {
                    this.locked = false;
                }
            });
    }
}

export class BaseAvailsReportController<T> {
    static $inject = ["$scope", "$filter", "reportService", "notifyService", "entityService"];

    exportUrl: string;

    startDate: string;
    endDate: string;
    mode: number;
    titleQuery: string;
    selectedChannelIds: number[];
    selectedChannels: string[];

    selectedMediaIds: number[];
    selectedMedias: Models.ITreeList2Item[];
    selectedTerritoryIds: number[];
    selectedTerritories: Models.ITreeList2Item[];
    selectedLanguageIds: number[];
    selectedLanguages: Models.ITreeList2Item[];

    selectedStatus: string;
    selectedTemplate: string;
    queryFields: string[];

    rows: T[];

    page: number;
    rowsPerPage: number;
    pageCount: number;
    initialized: boolean;
    perpetuity: boolean;
    expandFilters: boolean;
    loading: boolean;
    numFound: number;

    languageGroups: Models.ICharacteristicValueGroup[];
    selectedLanguageGroup: Models.ICharacteristicValueGroup;
    listOfLanguages: Models.ICharacteristicMetaDataValue[];
    languageTagLabel: string;

    territoryGroups: Models.ICharacteristicValueGroup[];
    selectedTerritoryGroup: Models.ICharacteristicValueGroup;
    listOfTerritories: Models.ICharacteristicMetaDataValue[];
    territoryTagLabel: string;

    mediaGroups: Models.ICharacteristicValueGroup[];
    selectedMediaGroup: Models.ICharacteristicValueGroup;
    listOfMedias: Models.ICharacteristicMetaDataValue[];
    mediaTagLabel: string;

    templates: Models.IKeyValuePair<string, number>[];
    statuses: Models.IKeyValuePair<string, number>[];
    templateFacets: { key: string; value: string; count: number }[];
    statusFacets: { key: string; value: string; count: number }[];

    showExport: boolean;

    constructor(private $scope: ng.IScope, $filter: ng.IFilterService, reportService: ReportService, notifyService: NotifyService,
        entityService: EntityService) {

        var now = moment();

        this.showExport = true;
        this.loading = false;
        this.startDate = now.format("L");
        this.endDate = now.add("days", 30).format("L");
        this.mode = 0;
        this.selectedTerritoryIds = [];
        this.selectedMediaIds = [];
        this.selectedLanguageIds = [];
        this.selectedChannelIds = [];
        this.selectedLanguages = [];
        this.page = 1;
        this.numFound = 0;
        this.pageCount = 10;
        this.initialized = false;
        this.perpetuity = false;
        this.queryFields = [];
        this.titleQuery = "";

        this.rows = [];

        this.rowsPerPage = 20;
        this.expandFilters = true;
        this.selectedMedias = [];
        this.selectedTerritories = [];

        this.templates = [];
        this.statuses = [];
        this.templateFacets = [];
        this.selectedTemplate = null;
        this.statusFacets = [];
        this.selectedStatus = null;

        $scope.$watch(() => this.templates, (newValue, oldValue) => {
            if (newValue != oldValue) {
                this.templateFacets = $filter<Filters.IFacetFilter>('facetFilter')(newValue);
            }
        });

        $scope.$watch(() => this.statuses, (newValue, oldValue) => {
            if (newValue != oldValue) {
                this.statusFacets = $filter<Filters.IFacetFilter>('facetFilter')(newValue);
            }
        });

        var templateID = "d0c3t1";
        notifyService.startProgress();
        entityService.getEmptyEntityCharData(templateID)
            .then((result) => {
                var tCmd = _.find(result.templateMetaData.characteristicMetaDatas, (cmd2) => cmd2.characteristicID == 2);
                if (tCmd != null) {
                    this.territoryTagLabel = tCmd.tagLabel;

                    entityService.getCharLovMetaData(tCmd.charValueSourceID, tCmd.charValueSetID)
                        .then((result2) => {
                            this.listOfTerritories = result2.listOfValues;
                            this.territoryGroups = result2.charValueGroups;
                            this.selectedTerritories.push(this.listOfTerritories[0]);
                            this.selectedTerritoryIds.push(this.listOfTerritories[0].characteristicValueID);
                        });

                }

                var cmd = _.find(result.templateMetaData.characteristicMetaDatas, (cmd2) => cmd2.characteristicID == 1);
                if (cmd != null) {
                    this.mediaTagLabel = cmd.tagLabel;
                    entityService.getCharLovMetaData(cmd.charValueSourceID, cmd.charValueSetID)
                        .then((result2) => {
                            this.listOfMedias = result2.listOfValues;
                            this.mediaGroups = result2.charValueGroups;
                        });

                }

                //this.selectedMedias.push(this.listOfMedias[0]);
                //this.selectedMediaIds.push(this.listOfMedias[0].characteristicValueID);

                var lCmd = _.find(result.templateMetaData.characteristicMetaDatas, (cmd2) => cmd2.characteristicID == 3);
                if (lCmd != null) {
                    this.languageTagLabel = lCmd.tagLabel;
                    entityService.getCharLovMetaData(lCmd.charValueSourceID, lCmd.charValueSetID)
                        .then((result2) => {
                            this.listOfLanguages = result2.listOfValues;
                            this.languageGroups = result2.charValueGroups;
                        });

                }

                //this.selectedLanguages.push(this.listOfLanguages[0]);
                //this.selectedLanguageIds.push(this.listOfLanguages[0].characteristicValueID);
            })
            .finally(() => {
                notifyService.endProgress();
            });
    }

    LanguageGroupChanged(group: Models.ICharacteristicValueGroup) {
        var newValueIDs = group.charValueAssociations.map(cva => cva.charValueID);

        while (this.selectedLanguageIds.pop()) {

        }
        Array.prototype.splice.apply(this.selectedLanguageIds, [0, 0].concat(newValueIDs));
        this.$scope.$broadcast(Consts.Events.TreeListShowSelected);
    }

    TerritoryGroupChanged(group: Models.ICharacteristicValueGroup) {
        var newValueIDs = group.charValueAssociations.map(cva => cva.charValueID);
        while (this.selectedTerritoryIds.pop()) {

        }
        Array.prototype.splice.apply(this.selectedTerritoryIds, [0, 0].concat(newValueIDs));
        this.$scope.$broadcast(Consts.Events.TreeListShowSelected);
    }

    MediaGroupChanged(group: Models.ICharacteristicValueGroup) {
        if (group && group.charValueAssociations) {
            var newValueIDs = group.charValueAssociations.map(cva => cva.charValueID);
            while (this.selectedMediaIds.pop()) {

            }
            Array.prototype.splice.apply(this.selectedMediaIds, [0, 0].concat(newValueIDs));
            this.$scope.$broadcast(Consts.Events.TreeListShowSelected);
        }
    }

    Search() {
        throw new Error("ERROR: Search must be defined in derived class");
    }

    GetExportUrl() {
        throw new Error("ERROR: GetExportUrl must be defined in derived classes");
    }

    GetSelectedFacetID(facet: string) {
        var id = -1;
        if (facet != null && facet != "") {
            var split = facet.split("|");
            if (split[1] != null) {
                id = parseInt(split[1]);
            }
        }

        return id;
    }

    _startSearch() {

    }

    _endSearch() {

    }

    // TODO: Probably define a model for ReportRequests
    GetQueryString(object: any) {
        var queryString = "";
        for (var key in object) {

            var value = "";
            var objValue = object[key];
            if (objValue == undefined || _.isNaN(objValue)) {
                value = "";
            } else if (_.isArray(objValue)) {
                value = object[key];
            } else if (_.isObject(objValue)) {
                value = JSON.stringify(objValue);
            } else {
                value = objValue;
            }

            if (queryString == "") {
                queryString += "?" + key + "=" + value;
            }
            else {
                queryString += "&" + key + "=" + value;
            }
        }
        return queryString;
    }

    PerpetuityChecked() {
        if (this.perpetuity) {
            this.endDate = '09/09/9999';
        }
    }
}


export class DealSearchReportController extends BaseAvailsReportController<Models.IEntitySearchDoc> {
    static $inject = ["$scope", "$filter", "reportService", "notifyService", "entityService", "rightsFilterService"];

    filterModel: Models.IRightsFilterModel;

    constructor($scope: ng.IScope, private $filter: ng.IFilterService, private reportService: ReportService, private notifyService: NotifyService,
        private entityService: EntityService, private rightsFilterService: RightsFilterService) {
        super($scope, $filter, reportService, notifyService, entityService);

        this.exportUrl = "/api/report/ExportRightsDeals";

        var startMoment = moment();
        var endMoment = startMoment.clone().add(moment.duration({ "months": 1 }));

        this.filterModel = this.rightsFilterService.GetCachedFilter({
            startDate: startMoment.format("L"),
            endDate: endMoment.format("L"),
            selectedTerritoryIDs: [],
            selectedMediaIDs: [],
            selectedChannelIDs: [],
            selectedLanguageIDs: [],
            exclusive: "1",
            exactInd: "0",
            assetCharID: null,
            selectedAssetCharValueIDs: [],
            titleKeywords: "",
            dimFormat: { "1": true, "2": true, "3": true, "4": true }
        });

        $scope.$watch(() => this.page, (newValue, oldValue) => {
            if (newValue != oldValue) {
                this.Search(false);
            }
        });
    }

    GetExportUrl() {
        var templateID = this.GetSelectedFacetID(this.selectedTemplate);
        var statusID = this.GetSelectedFacetID(this.selectedStatus);

        var clientStartDate = this.filterModel.startDate;
        var clientEndDate = this.filterModel.endDate;

        //Get the user's date format and then turn it back into mm/dd/yyyy
        var dateFormat: string = moment.localeData()["_longDateFormat"]["L"];
        var startDate = moment(clientStartDate, dateFormat).format('MM/DD/YYYY')
        var endDate = moment(clientEndDate, dateFormat).format('MM/DD/YYYY')

        var query = {
            mode: _.parseInt(this.filterModel.exactInd),
            startDate: startDate,
            endDate: endDate,
            territoryIds: this.filterModel.selectedTerritoryIDs,
            mediaIds: this.filterModel.selectedMediaIDs,
            channelIds: this.filterModel.selectedChannelIDs,
            languageIds: this.filterModel.selectedLanguageIDs,
            templateIds: "",
            statusIds: statusID
        }

        var url = this.exportUrl;
        url += this.GetQueryString(query);

        return url;
    }

    Search(resetPage: boolean = true) {
        if (resetPage) this.page = 1;

        var templateID = this.GetSelectedFacetID(this.selectedTemplate);
        var statusID = this.GetSelectedFacetID(this.selectedStatus);

        var clientStartDate = this.filterModel.startDate;
        var clientEndDate = this.filterModel.endDate;

        //Get the user's date format and then turn it back into mm/dd/yyyy
        var dateFormat: string = moment.localeData()["_longDateFormat"]["L"];
        var startDate = moment(clientStartDate, dateFormat).format('MM/DD/YYYY')
        var endDate = moment(clientEndDate, dateFormat).format('MM/DD/YYYY')

        this.loading = true;
        this.notifyService.startProgress();
        this.reportService.getRightsDeals(_.parseInt(this.filterModel.exactInd), startDate, endDate, this.filterModel.selectedTerritoryIDs,
            this.filterModel.selectedMediaIDs, this.filterModel.selectedChannelIDs, this.filterModel.selectedLanguageIDs, templateID, statusID, this.filterModel.titleKeywords,
            this.page, this.rowsPerPage, this.queryFields)
            .then((result) => {
                this.page = result.page;
                this.rows = result.resultDocs;
                this.pageCount = result.pageCount;
                this.templates = result.templateFacets;
                this.statuses = result.statusFacets;

                this.numFound = result.numFound;
            })
            .finally(() => {
                this.loading = false;
                this.notifyService.endProgress();
                this.initialized = true;
            });
    }

    Clear() {
        this.page = 1
        this.rows = [];
        this.pageCount = 0;
        this.templates = [];
        this.statuses = [];
        this.numFound = 0;
    }
}

export class QuickLinksMenuController {

    static $inject = ["$window", "favoriteService", "notifyService", "eventService"];
    links: Models.IFavoriteLink[];

    constructor(private $window: ng.IWindowService, private favoriteService: FavoriteService, private notifyService: NotifyService, private eventService: EventService) {
        this.GetQuickLinks();

        eventService.on(Consts.Events.QuickLinksRefresh, () => {
            this.GetQuickLinks();
        });
    }

    Remove(link: Models.IFavoriteLink, $event: ng.IAngularEvent) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.$window.confirm("Are you sure?")) {
            this.favoriteService.removeUrl(link.name, link.url);
            _.remove(this.links, l => l.name == link.name && l.url == link.url);
            this.notifyService.notifySuccess("Link " + link.name + " Removed");
        }
    }

    GetQuickLinks() {
        this.favoriteService.getLinks().then((result) => {
            if (result != null) {
                this.links = [];
                var quickLinks = JsonUtil.Parse(result.savedReportsJson);

                for (var key in quickLinks) {
                    var values = quickLinks[key];
                    _.forEach(values, (value) => {
                        let quickLink: Models.IFavoriteLink = {
                            name: key,
                            url: value
                        };
                        this.links.push(quickLink);
                    });
                }
            }
        });
    }
}

export class ChartController {
    static $inject = ["$scope", "$window", "notifyService", "entityConfigService"];
    templates: Models.ICharacteristicTemplate[];
    selectedTemplate: Models.ICharacteristicTemplate;
    charTypeID: number;

    constructor(private $scope: ng.IScope, private $window: ng.IWindowService, private notifyService: NotifyService, private entityConfigService: EntityConfigService) {
        this.templates = [];
        this.selectedTemplate = null;
        this.charTypeID = null;
    }

    Init(charTypeID: number) {

        this.charTypeID = charTypeID;

        this.entityConfigService.getCharacteristicTemplates(charTypeID)
            .then((result) => {
                this.templates = result;
                this.selectedTemplate = this.templates[0];
            });
    }
}

export class TestTreeController {
    static $inject = ["$scope", "entityService", "notifyService"];

    templateID: string;
    charID: number;
    selectedValues: number[];

    filterText: string;

    tagLabel: string;
    listOfValues: Models.ICharacteristicMetaDataValue[];

    constructor(private $scope: ng.IScope, private entityService: EntityService, private notifyService: NotifyService) {

        $scope.$watchCollection(() => [this.templateID, this.charID],
            (newValue, oldValue) => {
                if (!_.isEmpty(this.templateID) && this.charID > 0) {
                    this.notifyService.startProgress();
                    this.entityService.getEmptyEntityCharData(this.templateID)
                        .then((result) => {
                            var cmd = _.find(result.templateMetaData.characteristicMetaDatas, (cmd2) => cmd2.characteristicID == this.charID);
                            if (cmd != null) {
                                this.tagLabel = cmd.tagLabel;
                                return this.entityService.getCharLovMetaData(cmd.charValueSourceID, cmd.charValueSetID)
                                    .then((result2) => {
                                        this.listOfValues = result2.listOfValues;
                                    });
                            }
                        })
                        .finally(() => {
                            this.notifyService.endProgress();
                        });

                }
            });

        $scope.$watchCollection<number[]>(() => this.selectedValues,
            (newValue, oldValue) => {
                if (newValue) {
                    for (var i = 0; i < newValue.length; i++) {
                        if (!_.isNumber(newValue[i])) {
                            newValue[i] = _.parseInt(newValue[i].toString());
                        }
                    }
                }
            });

        this.listOfValues = [];
        this.filterText = "";
        this.templateID = "d13c3t1";
        this.charID = 2;
        this.selectedValues = [300053, 3000252, 30000222];
    }

    RemoveValue(lov: Models.ICharacteristicMetaDataValue, $event: ng.IAngularEvent) {
        $event.preventDefault();
        _.remove(this.selectedValues, v => v == lov.characteristicValueID);
    }
}

export class RoyaltyStatementController {
    static $inject = ["$scope", "royaltyService", "notifyService", "entityConfigService", "contactService", "dateLocaleService"];

    startDate: string;
    endDate: string;
    docTypeIDs: number[];
    incomeGroupIDs: number[];
    contactID: number;
    payees: Models.IGetRoyaltyStatement[];
    currentPayee: Models.IGetRoyaltyStatement;
    payeeStatement: Models.IGetRoyaltyRecipientResponse;
    amountTemplates: Models.ICharacteristicTemplate[];
    invoiceTemplates: Models.ICharacteristicTemplate[];
    exportOptions: string[];
    companies: string[];

    payeeFilter: string;
    selectedDocumentTypes: Models.ICharacteristicTemplate[];
    selectedIncomeGroups: Models.ICharacteristicTemplate[];
    selectedExportOption: string;
    selectedCompany: string;
    loading: boolean;
    selectedUser: Models.IEntity;
    exportView: string[];
    selectedExportView: string;

    constructor(private $scope: ng.IScope, private royaltyService: RoyaltyService, private notifyService: NotifyService, private entityConfigService: EntityConfigService, private contactService: ContactService, private dateLocaleService: DateLocaleService) {
        this.docTypeIDs = [];
        this.incomeGroupIDs = [];
        this.payeeFilter = "";
        this.payees = [];
        this.loading = false;


        this.Init();
    }

    Init() {
        this.entityConfigService.getCharacteristicTemplates(Consts.CharTypeIDs.amount)
            .then((result) => {
                this.amountTemplates = result;
            });

        this.entityConfigService.getCharacteristicTemplates(Consts.CharTypeIDs.invoice)
            .then((result) => {
                this.invoiceTemplates = result;
            });

        this.exportOptions = [
            ".pdf",
            ".doc"
        ]
        this.selectedExportOption = this.exportOptions[0];

        this.companies = [
            "US",
            "UK"
        ]
        this.selectedCompany = this.companies[0];

        this.exportView = ["Payee", "Title"]
        this.selectedExportView = this.exportView[0];

    }

    Search() {
        this.notifyService.startProgress();
        this.loading = true;
        this.payeeStatement = null;
        this.payees = [];
        var startDateMoment = this.dateLocaleService.parseMoment(this.startDate);//  moment(this.startDate);
        var endDateMoment = this.dateLocaleService.parseMoment(this.endDate);
        var usStartDate = this.dateLocaleService.delocalizeMoment(startDateMoment).format("L");
        var usEndDate = this.dateLocaleService.delocalizeMoment(endDateMoment).format("L");

        var request: Models.IGetRoyaltyStatementsRequest = {
            startDate: usStartDate,
            endDate: usEndDate,
            docTypeIDs: this.selectedDocumentTypes.map<number>((doc) => {
                return doc.templateID;
            }),
            incomeGroupIDs: this.selectedIncomeGroups.map<number>((group) => {
                return group.templateID;
            }),
            companyID: this.selectedCompany == "US" ? 10 : 20,
            contactID: this.selectedUser != null ? this.selectedUser.recordID : -1
        }

        this.royaltyService.getRoyaltyStatements(request)
            .then((result) => {
                this.payees = result.billingContacts;
                if (this.payees.length > 0) {
                    this.SelectPayee(this.payees[0]);
                }
            })
            .finally(() => {
                this.loading = false;
                this.notifyService.endProgress();
            });
    }

    GetCharacteristic(tagLabel: string) {
        var wtf: any = this.payeeStatement.contact.characteristics;
        var charID = 0;
        for (var i = 0; i < wtf.templateMetaData.characteristicMetaDatas.length; i++) {
            var w = wtf.templateMetaData.characteristicMetaDatas[i];
            if (w.tagLabel == tagLabel) {
                charID = w.characteristicID;
            }
        }

        for (var j = 0; j < wtf.characteristicDatas.length; j++) {
            var y = wtf.characteristicDatas[j];
            if (y.charactersticID == charID) {
                return y.value;
            }
        }
    }

    Users(query: string) {
        return this.contactService.getSuggestions(query);
    }

    Export() {
        var exportType = this.selectedExportOption == ".pdf" ? 40 : 20;
        var exportView = this.selectedExportView == "Payee" ? 1 : 2;
        var startDateMoment = this.dateLocaleService.parseMoment(this.startDate);//  moment(this.startDate);
        var endDateMoment = this.dateLocaleService.parseMoment(this.endDate);
        var usStartDate = this.dateLocaleService.delocalizeMoment(startDateMoment).format("L");
        var usEndDate = this.dateLocaleService.delocalizeMoment(endDateMoment).format("L");
        this.notifyService.startProgress();
        var request: Models.IGetRoyaltyStatementsRequest = {
            endDate: usEndDate,
            startDate: usStartDate,
            docTypeIDs: this.selectedDocumentTypes.map<number>((doc) => {
                return doc.templateID;
            }),
            incomeGroupIDs: this.selectedIncomeGroups.map<number>((group) => {
                return group.templateID;
            }),
            contactID: this.selectedUser != null ? this.selectedUser.recordID : -1,
            companyID: this.selectedCompany == "US" ? 10 : 20,
            exportDocType: exportType,
            exportView: exportView
        }

        this.royaltyService.exportRoyaltyStatement(request)
            .then((result) => {
                alert("An email with a link to download this Royalty Statement has been sent to your email address.");
            })
            .finally(() => {
                this.notifyService.endProgress();
            });

    }

    SelectPayee(payee: Models.IGetRoyaltyStatement) {
        this.loading = true;
        this.notifyService.startProgress();
        this.currentPayee = payee;
        this.payeeStatement = null;
        var startDateMoment = this.dateLocaleService.parseMoment(this.startDate);//  moment(this.startDate);
        var endDateMoment = this.dateLocaleService.parseMoment(this.endDate);
        var usStartDate = this.dateLocaleService.delocalizeMoment(startDateMoment).format("L");
        var usEndDate = this.dateLocaleService.delocalizeMoment(endDateMoment).format("L");
        var request: Models.IGetRoyaltyStatementsRequest = {
            endDate: usEndDate,
            startDate: usStartDate,
            docTypeIDs: this.selectedDocumentTypes.map<number>((doc) => {
                return doc.templateID;
            }),
            incomeGroupIDs: this.selectedIncomeGroups.map<number>((group) => {
                return group.templateID;
            }),
            contactID: payee.contact.recordID,
            companyID: this.selectedCompany == "US" ? 10 : 20
        }

        this.royaltyService.getRoyaltyStatement(request)
            .then((result) => {
                this.payeeStatement = result;
            })
            .finally(() => {
                this.loading = false;
                this.notifyService.endProgress();
            });
    }
}

export class AvailabilityExplorerController {

    static $inject = ["$scope", "$timeout", "$q", "$location", "$anchorScroll", "reportService", "entityService", "entityConfigService", "notifyService", "dateLocaleService", "rightsFilterService"];

    assetData: Models.IAvailsTimelineAssetsRightsGroup[];                      // asset data for chart 
    summaryData: Models.IAvailsTimelineSummaryWindow[];       // summary data for chart

    templateID: string = "d0c3t1";                              // templateID for getting dimensions


    //startDate: string;                                          // start date
    //endDate: string;                                            // end date
    //exclusive: string;                                          // exclusibity
    //exactInd: string;                                           // flexibility

    filterModel: Models.IRightsFilterModel;

    loading: boolean;                                           // loading flag

    territoryDictionary: { [key: number]: string };             // dictionary territory names (for quick lookup)
    mediaDictionary: { [key: number]: string };                 // dictionary media names (for quick lookup)
    channelDictionary: { [key: number]: string };               // dictionary channel names (for quick lookup)
    languageDictionary: { [key: number]: string };              // dictionary language names (for quick lookup)
    clientDictionary: { [dealID: number]: string };             // dictionary client names by dealID (for quick lookup)
    dealTitleDictionary: { [dealID: number]: string };          // dictionary deal titles by dealID (for quick lookup)
    rightsTemplateDictionary: { [rightsID: number]: string };   // dictionary of right template names by rightsetID (for quick lookup)

    detailsDealID: number;                                      // current details deal ID
    detailsRightsID: number;                                    // current details rightset ID
    detailsLoading: boolean;                                    // details loading flag
    detailsInfo: Models.IAvailSearchDetailResponse;             // current details info
    highlightDetailsRightsID: number;

    timeoutPromises: { [rightsetID: number]: ng.IPromise<any> };    // dictionary of promises by rightsetID


    rightSets: Models.IAvailsTimelineGroupRightset[];              // righsets of asset data unioned and sorted

    pageSize: number = 10;
    page: number = 1;
    assetCount: number = 0;
    rowCount: number = 0;
    pageCount: number = 0;

    provisionalRecID: number;
    addProvisionalModal: boolean;
    provAssetIDs: number[];
    provChannelIDs: number[];
    provLanguageIDs: number[];
    provMediaIDs: number[];
    provTerritoryIDs: number[];
    provRightSetTemplateID: number;
    provStartDate: string;
    provEndDate: string;
    provDealTitle: string;
    provClientTitle: string;
    provExclusive: string;
    provFormValid: boolean;

    //editRightSet: Models.IAvailSearchAssetRightSet;
    modalTitle: string;

    constructor(private $scope: ng.IScope, private $timeout: ng.ITimeoutService, private $q: ng.IQService,
        private $location: ng.ILocationService, private $anchorScroll: ng.IAnchorScrollService, private reportService: ReportService,
        private entityService: EntityService, private entityConfigService: EntityConfigService, private notifyService: NotifyService,
        public dateLocaleService: DateLocaleService, private rightsFilterService: RightsFilterService) {

        var startMoment = moment().subtract(moment.duration({ "months": 1 }));
        var endMoment = startMoment.clone().add(moment.duration({ "years": 1 }));
        // start date defaults to today
        var startDate = startMoment.format("L");
        // end date defaults to +1 year from today
        var endDate = endMoment.format("L");

        this.filterModel = this.rightsFilterService.GetCachedFilter({
            startDate: startDate,
            endDate: endDate,
            selectedAssetRecordIDs: [],
            exactInd: "0",
            exclusive: "1",
            selectedChannelIDs: [],
            selectedAssetFacet: {},
            selectedLanguageIDs: [],
            selectedMediaIDs: [],
            selectedTerritoryIDs: [],
            selectedRightSetTemplateIDs: [],
            selectedTerritoryLeafIDs: [],
            selectedMediaLeafIDs: [],
            selectedChannelLeafIDs: [],
            selectedLanguageLeafIDs: [],
            show: {
                channels: true,
                clients: true,
                dealIDs: true,
                languages: true,
                medias: true,
                territories: true
            },
            matchType: 1,
            isWindowed: false,
            dimFormat: { "1": true, "2": true, "3": true, "4": true }
        });

        this.loading = false;

        this.territoryDictionary = {};
        this.mediaDictionary = {};
        this.channelDictionary = {};
        this.languageDictionary = {};
        this.clientDictionary = {};
        this.dealTitleDictionary = {};
        this.rightsTemplateDictionary = {};

        this.detailsDealID = null;
        this.detailsRightsID = null;
        this.detailsLoading = false;
        this.detailsInfo = null;


        this.timeoutPromises = {};

        this.rightSets = [];

        this.provisionalRecID = -1;
        this.addProvisionalModal = false;
        this.provAssetIDs = [];
        this.provChannelIDs = [];
        this.provLanguageIDs = [];
        this.provMediaIDs = [];
        this.provTerritoryIDs = [];
        this.provRightSetTemplateID = 99;
        this.provStartDate = "";
        this.provEndDate = "";
        this.provDealTitle = "";
        this.provClientTitle = "";
        this.provExclusive = "0";
        this.provFormValid = false;

        this.modalTitle = "";
        //this.editRightSet = null;



        // union/sort rightsets when asset data changes
        $scope.$watchCollection(() => this.assetData,
            (newValue, oldValue) => {
                var rightsets = _.selectMany(this.assetData, (ad) => ad.rightsets);
                var groupDictionary = _.groupBy(rightsets, (rs) => rs.rightsetID);

                this.rightSets = _(groupDictionary).values().map(values => values[0]).sortByAll(rs => rs.templateGroupID, rs => rs.startDate).valueOf();
            });



        $scope.$watch(() => this.addProvisionalModal, (newValue, oldValue) => {
            if (!newValue && oldValue) {
                this.provAssetIDs = [];
                this.provChannelIDs = [];
                this.provLanguageIDs = [];
                this.provMediaIDs = [];
                this.provTerritoryIDs = [];
                this.provRightSetTemplateID = 99;
                this.provStartDate = "";
                this.provEndDate = "";
                this.provDealTitle = "";
                this.provClientTitle = "";
                this.provExclusive = "0";

                //this.editRightSet = null;
            }
        });

        $scope.$watch(() => [this.provAssetIDs, this.provLanguageIDs, this.provMediaIDs, this.provTerritoryIDs, this.provStartDate, this.provEndDate],
            (newValue, oldValue) => {
                var valid = true;
                valid = valid && this.provAssetIDs.length > 0;
                valid = valid && this.provLanguageIDs.length > 0;
                valid = valid && this.provMediaIDs.length > 0;
                valid = valid && this.provTerritoryIDs.length > 0;
                valid = valid && moment(new Date(this.provEndDate)).isAfter(moment(new Date(this.provStartDate)));
                this.provFormValid = valid;

            }, true);
    }



    Init() {
        this.notifyService.startProgress();
        this.entityConfigService.getRightsDimensions()
            .then((result) => {
                for (let i = 1; i <= 4; i++) {
                    var characteristic = result.characteristics[i];
                    var lovs = result.lovs[i];
                    if (characteristic != null && lovs != null) {
                        switch (i) {
                            case 1:
                                _.foreachGraph(lovs.listOfValues, (lov) => lov.childValues, (lov) => this.mediaDictionary[lov.characteristicValueID] = lov.characteristicValueLabel);
                                break;
                            case 2:
                                _.foreachGraph(lovs.listOfValues, (lov) => lov.childValues, (lov) => this.territoryDictionary[lov.characteristicValueID] = lov.characteristicValueLabel);
                                break;
                            case 3:
                                _.foreachGraph(lovs.listOfValues, (lov) => lov.childValues, (lov) => this.languageDictionary[lov.characteristicValueID] = lov.characteristicValueLabel);
                                break;
                            case 4:
                                _.foreachGraph(lovs.listOfValues, (lov) => lov.childValues, (lov) => this.channelDictionary[lov.characteristicValueID] = lov.characteristicValueLabel);
                                break;
                            default:
                                break;
                        }
                    }
                }
                this.notifyService.endProgress();
            });
    }

    ClickSearch() {
        this.page = 1;
        this.Search();
    }
    PageChanged() {
        this.$timeout(() => {
            this.Search();
        });
    }
    PageSizeChanged() {
        this.page = 1;
        this.$timeout(() => {
            this.Search();
        });
    }

    Search() {
        var clientStartDate = this.filterModel.startDate;
        var clientEndDate = this.filterModel.endDate;

        //Get the user's date format and then turn it back into mm/dd/yyyy
        var dateFormat: string = moment.localeData()["_longDateFormat"]["L"];
        var startDate = moment(clientStartDate, dateFormat).format('MM/DD/YYYY')
        var endDate = moment(clientEndDate, dateFormat).format('MM/DD/YYYY')

        var assetFacets = this.filterModel.selectedAssetFacet;
        var exclusive = this.filterModel.exclusive == "" ? true : this.filterModel.exclusive == "1";
        var exactInd = _.parseInt(this.filterModel.exactInd);
        var isWindowed = this.filterModel.isWindowed;
        var territoryIDs = this.filterModel.selectedTerritoryLeafIDs;
        var mediaIDs = this.filterModel.selectedMediaLeafIDs;
        var channelIDs = this.filterModel.selectedChannelLeafIDs;
        var languageIDs = this.filterModel.selectedLanguageLeafIDs;
        var assetIDs = this.filterModel.selectedAssetRecordIDs;
        var rightSetTemplateIDs = this.filterModel.selectedRightSetTemplateIDs;
        var minDays = this.filterModel.minDays;
        var matchType = this.filterModel.matchType;
        var offset = (this.page - 1) * this.pageSize;
        var limit = this.pageSize;


        this.loading = true;
        this.notifyService.startProgress();
        this.reportService.getAvailTimeline(startDate, endDate, assetIDs, assetFacets, exclusive, exactInd, isWindowed, rightSetTemplateIDs, territoryIDs, mediaIDs, channelIDs, languageIDs, minDays, matchType, offset, limit)
            .then((results) => {
                return this.reportService.getBackgroundReportResults<Models.IAvailsTimelineResponse>(results.reportID)
                    .then((data) => {
                        this.assetData = data.assetRightsGroups;
                        this.summaryData = data.summaryWindows;
                        this.clientDictionary = data.clientNames;
                        this.dealTitleDictionary = data.dealTitles;
                        this.rightsTemplateDictionary = data.rightsTemplates;
                        this.assetCount = data.assetCount;
                        this.rowCount = data.assetCount;
                        this.pageCount = Math.ceil(this.rowCount / this.pageSize);
                    })
                    .catch((reason) => {
                        this.assetData = [];
                        this.summaryData = [];
                        this.clientDictionary = {};
                        this.dealTitleDictionary = {};
                        this.rightsTemplateDictionary = {};
                        this.assetCount = 0;
                        this.rowCount = 0;
                        this.pageCount = 0;
                    });
            })
            .finally(() => {
                this.loading = false;
                this.notifyService.endProgress();
            });

    }

    ClearResults() {
        this.assetData = [];
        this.summaryData = [];
        this.clientDictionary = {};
        this.dealTitleDictionary = {};
        this.rightsTemplateDictionary = {}
        this.assetCount = 0;
        this.rowCount = 0;
        this.pageCount = 0;
    }

    ShowDetails(rightSetAvail: Models.IAvailsTimelineGroupRightset) {

        var dealID = rightSetAvail.dealID;
        var rightSetID = rightSetAvail.rightsetID;
        this.detailsInfo = null;
        this.detailsDealID = dealID;
        this.detailsRightsID = rightSetID;

        // put this 500ms timeout into a dictionary per rightSetID
        this.timeoutPromises[rightSetAvail.rightsetID] = this.$timeout(() => {

            this.detailsLoading = true;
            this.notifyService.startProgress();
            this.reportService.getAvailSearchDetail(dealID, rightSetID)
                .then((result) => {
                    this.detailsInfo = result;
                })
                .finally(() => {
                    this.detailsLoading = false;
                    this.notifyService.endProgress();
                });

        }, 500);
    }

    CancelDetails(rightSetAvail: Models.IAvailsTimelineGroupRightset) {
        // set detailsInfo to null again
        this.$timeout(() => {
            this.detailsInfo = null;
        });
        // cancel any previous unfulfilled timeout for this rightsetID
        this.$timeout.cancel(this.timeoutPromises[rightSetAvail.rightsetID]);
    }





    // highlights the selected rightset in the table below the chart
    GoToDetailRow(rightSetAvail: Models.IAvailsTimelineGroupRightset) {

        this.$timeout(() => {
            this.detailsDealID = rightSetAvail.dealID;
            this.detailsRightsID = rightSetAvail.rightsetID;
            this.highlightDetailsRightsID = rightSetAvail.rightsetID;
        });
    }


    NewProvisional() {
        this.modalTitle = "Add a Provisional Deal";
        this.addProvisionalModal = true;
    }

    SaveProvisional() {

        //if (this.editRightSet != null) {


        //    this.editRightSet.assetIDs = this.provAssetIDs;
        //    this.editRightSet.channelIDs = this.provChannelIDs;
        //    this.editRightSet.languageIDs = this.provLanguageIDs;
        //    this.editRightSet.mediaIDs = this.provMediaIDs;
        //    this.editRightSet.territoryIDs = this.provTerritoryIDs;
        //    this.editRightSet.startDate = moment(this.provStartDate).format();
        //    this.editRightSet.endDate = moment(this.provEndDate).format();
        //    this.dealTitleDictionary[this.editRightSet.dealID] = this.provDealTitle;
        //    this.clientDictionary[this.editRightSet.dealID] = this.provClientTitle;

        //}
        //else {

        //    var recID = this.provisionalRecID--;

        //    this.rightsTemplateDictionary[recID] = "Provisional";
        //    this.dealTitleDictionary[recID] = this.provDealTitle;
        //    this.clientDictionary[recID] = this.provClientTitle;

        //    //var rightSetAvail: Models.IAvailSearchAssetRightSet = {
        //    //    assetIDs: this.provAssetIDs,
        //    //    channelIDs: this.provChannelIDs,
        //    //    languageIDs: this.provLanguageIDs,
        //    //    mediaIDs: this.provMediaIDs,
        //    //    territoryIDs: this.provTerritoryIDs,
        //    //    dealID: recID,
        //    //    rightSetID: recID,
        //    //    rightSetTemplateID: 99,
        //    //    startDate: moment(this.provStartDate).format(),
        //    //    endDate: moment(this.provEndDate).format()
        //    //};

        //    // add rightSetAvail to any matching assets
        //    //_.filter(this.assetData,(ad) => _.contains(rightSetAvail.assetIDs, ad.assetRecID))
        //    //    .forEach((ad) => ad.assetRightSets.push(_.cloneDeep(rightSetAvail)));

        //}
        console.warn("better fix me up");



        // trigger redraw on chart directive
        this.assetData = _.cloneDeep(this.assetData);

        this.addProvisionalModal = false;
    }

    EditRightSet(rightSet: Models.IAvailsTimelineGroupRightset) {
        this.modalTitle = "Edit Deal";
        console.warn("gotta fix this");
        //this.editRightSet = rightSet;
        //var tempRightSet = _.clone(this.editRightSet);
        //this.provAssetIDs = tempRightSet.assetIDs;
        //this.provChannelIDs = tempRightSet.channelIDs;
        //this.provLanguageIDs = tempRightSet.languageIDs;
        //this.provMediaIDs = tempRightSet.mediaIDs;
        //this.provTerritoryIDs = tempRightSet.territoryIDs;
        //this.provStartDate = moment(tempRightSet.startDate).format("L");
        //this.provEndDate = moment(tempRightSet.endDate).format("L");
        //this.provDealTitle = this.dealTitleDictionary[tempRightSet.dealID];
        //this.provClientTitle = this.clientDictionary[tempRightSet.dealID];
        ////this.provExclusive = this.rightsExclusiveDictionary[tempRightSet.rightSetID] ? "1" : "0";

        //this.addProvisionalModal = true;
        //this.$scope.$broadcast(Consts.Events.TreeListShowSelected);
    }

    FormatID(input: number) {
        if (input > 0) {
            return input.toString();
        } else {
            return "[" + Math.abs(input) + "]";
        }
    }
}

export class AccountManageController {

    static $inject = ["$scope", "$window", "$q", "accountService", "notifyService", "entityService", "securityService"];

    googleModalOpen: boolean;

    entity: Models.IEntityCharDataPair;
    editModalOpen: boolean;

    secretKey: string;
    otpUrl: string;
    qrCodeSrc: string;

    code: string;

    username: string;
    isGoogleAuthenticatorEnabled: boolean;

    avatars: string[];
    selectedAvatar: string;
    savedAvatar: string;
    editAvatarModalOpen: boolean;
    showUploadEdit: boolean;

    visibility: number;
    incompleteProfile: boolean;

    dealContacts: { [key: string]: string };
    openContactsModal: boolean;
    initialized: boolean;

    apiAccessKeys: Models.IApiAuthKey[];
    apiSecretKey: string;
    password: string;

    constructor(private $scope: ng.IScope, private $window: ng.IWindowService, private $q: ng.IQService,
        private accountService: AccountService, private notifyService: NotifyService,
        private entityService: EntityService, private securityService: SecurityService) {

        this.visibility = 1;
        this.editModalOpen = false;

        this.googleModalOpen = false;
        this.secretKey = "";
        this.otpUrl = "";
        this.qrCodeSrc = "";
        this.code = "";
        this.username = "";
        this.isGoogleAuthenticatorEnabled = false;

        this.avatars = [];
        this.selectedAvatar = "";
        this.savedAvatar = "";
        this.editAvatarModalOpen = false;
        this.showUploadEdit = false;
        this.dealContacts = {};
        this.openContactsModal = false;
        this.initialized = false;
        this.incompleteProfile = false;
        this.password = "";

        $scope.$watch(() => this.googleModalOpen,
            (newValue, oldValue) => {
                if (!newValue && oldValue) {
                    this.secretKey = "";
                    this.otpUrl = "";
                }
            });

        $scope.$watch(() => this.otpUrl,
            (newValue, oldValue) => {
                if (newValue != oldValue) {
                    if (!_.isEmpty(this.otpUrl)) {
                        this.qrCodeSrc = this.otpUrl;
                    } else {
                        this.qrCodeSrc = "";
                    }
                }
            });
    }

    Init(entityID: string, inCompleteProfile: boolean) {
        this.LoadManageState()
            .then(() => {
                this.entityService.getEntity(entityID)
                    .then((res) => {
                        this.LoadAvatars();
                        this.LoadAccessKeys();
                        this.entity = res;
                    })
                    .finally(() => {
                        this.incompleteProfile = inCompleteProfile;
                        this.initialized = true;
                        if (this.incompleteProfile) {
                            this.editModalOpen = true;
                        }
                    });
            });
    }

    LoadAccessKeys() {
        this.notifyService.startProgress();
        this.securityService.getSecurityTokens()
            .then((res) => {
                if (!_.isEmpty(res)) {
                    this.apiAccessKeys = res;
                }
                else {
                    this.apiAccessKeys = null;
                }
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    RevokeAccessKey(authKey: Models.IApiAuthKey) {
        this.notifyService.startProgress();
        this.securityService.revokeSecurityTokens(authKey.apiAuthID)
            .then((res) => {
                this.LoadAccessKeys();
                this.notifyService.notifySuccess("Access keys revoked");
            })
            .catch((err) => {
                this.notifyService.notifyError("Error revoking keys");
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    GenerateAccessKeys() {
        this.notifyService.startProgress();
        this.securityService.generateSecurityTokens()
            .then((res) => {
                this.LoadAccessKeys();
                this.apiSecretKey = res.secretKey;
            })
            .catch((err) => {
                this.notifyService.notifyError("Error generating keys");
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    SaveProfile(isValid: boolean) {
        if (isValid) {
            this.notifyService.startProgress();
            this.entityService.saveUserProfileData(this.entity.charDataCollection.charDatas)
                .then(() => {
                    this.notifyService.notifySuccess("User Updated!");
                }, this.notifyService.errorResponseHandler)
                .finally(() => {
                    this.editModalOpen = false;
                    this.notifyService.endProgress();
                });
        }
    }

    LoadManageState() {
        this.notifyService.startProgress();
        return this.accountService.getAccountManageState()
            .then((result) => {
                this.username = result.username;
                this.isGoogleAuthenticatorEnabled = result.isGoogleAuthenticatorEnabled;
                this.savedAvatar = result.profileAvatar || "";
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    SetupGoogle($event: ng.IAngularEvent) {
        $event.preventDefault();
        this.notifyService.startProgress();
        this.accountService.generateGoogleAuthenticatorKey()
            .then((result) => {
                this.secretKey = result.secretKey;
                this.otpUrl = result.otpURL;
                this.googleModalOpen = true;
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    VerifyCode() {
        this.notifyService.startProgress();
        this.accountService.confirmGoogleAuthenticatorKey(this.secretKey, this.code)
            .then((result: any) => {
                if (result.success) {
                    this.notifyService.notifySuccess("Google Authenticator Verified");
                    return this.LoadManageState();
                } else {
                    this.notifyService.notifyError(result.message);
                    return this.$q.reject();
                }
            },
                () => {
                    this.notifyService.notifyError("Unable to Verify Code");
                    return this.$q.reject();
                })
            .then(
                () => {
                    this.googleModalOpen = false;
                },
                angular.noop)
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    RemoveGoogle($event: ng.IAngularEvent) {
        $event.preventDefault();
        if (this.$window.confirm("Are you sure you want to remove Google Authenticator?")) {
            this.notifyService.startProgress();
            this.accountService.removeGoogleAuthenticator(this.password)
                .then(() => {
                    this.notifyService.notifySuccess("Google Authenticator Removed");
                    return this.LoadManageState();
                },
                () => {
                        this.notifyService.notifyError("Failed to confirm password and remove Google Authenticator");
                })
                .finally(() => {
                    this.notifyService.endProgress();
                });

        }
    }

    LoadAvatars() {
        this.avatars = [];
        for (var x = 1; x < 19; x++) {
            for (var y = 1; y < 19; y++) {
                this.avatars.push("avatar" + y + "-" + x);
            }
        }
    }

    SelectAvatar(avatar: string) {
        if (this.selectedAvatar == avatar) {
            this.selectedAvatar = '';
        }
        else {
            this.selectedAvatar = avatar;
        }
    }
    SaveAvatar() {
        this.notifyService.startProgress();
        this.accountService.saveProfileAvatar(this.selectedAvatar)
            .then(() => {
                return this.LoadManageState()
                    .then(() => {
                        this.notifyService.notifySuccess("Avatar Saved");
                        this.editAvatarModalOpen = false;
                    });
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    AddDealSettingContacts() {

    }
}

export class NewPasswordController {

    static $inject = ["$scope"];

    password: string;
    confirmPassword: string;

    passwordValidators: { [key: string]: (value: string) => boolean };
    confirmPasswordValidators: { [key: string]: (value: string) => boolean };

    hasLength: boolean;
    hasNumber: boolean;
    hasUpper: boolean;
    hasLower: boolean;
    hasSymbol: boolean;
    hasGoodPassword: boolean;

    constructor($scope: ng.IScope) {
        this.password = "";
        this.confirmPassword = "";

        this.passwordValidators = {};
        this.confirmPasswordValidators = {};

        this.hasLength = false;
        this.hasNumber = false;
        this.hasUpper = false;
        this.hasLower = false;
        this.hasSymbol = false;
        this.hasGoodPassword = false;

        this.confirmPasswordValidators = {
            "mismatch": (newValue) => {
                return angular.equals(newValue, this.password);
            }
        };

        this.passwordValidators = {
            "format": (newValue) => {
                var value = newValue || "";
                this.hasLength = value.length >= 8;
                this.hasNumber = /[0-9]/.test(value);
                this.hasLower = /[a-z]/.test(value);
                this.hasUpper = /[A-Z]/.test(value);
                this.hasSymbol = /[^0-9a-zA-Z]/.test(value);
                this.hasGoodPassword = this.hasLength && this.hasLower && this.hasNumber && this.hasSymbol && this.hasUpper;
                return this.hasGoodPassword;
            }

        };


    }

}

export class AclTestController {

    static $inject = ["$scope", "$q", "notifyService", "securityService", "aclService", "accountService"];

    permissionType: string;
    permissionPath: string;

    read: boolean;
    write: boolean;
    create: boolean;
    del: boolean;

    uri: string;
    accessResult: number;
    permissionResult: number;

    readClass: string;
    writeClass: string;
    createClass: string;
    deleteClass: string;

    mode: string;
    acls: string[];
    useRealAcl: boolean;

    constructor(private $scope: ng.IScope, private $q: ng.IQService, private notifyService: NotifyService, private securityService: SecurityService, private aclService: AclService, private accountService: AccountService) {
        this.permissionType = "activity";
        this.permissionPath = "/site";

        this.read = false;
        this.write = false;
        this.create = false;
        this.del = false;
        this.uri = "";

        this.accessResult = 0;

        this.readClass = "btn-default";
        this.writeClass = "btn-default";
        this.createClass = "btn-default";
        this.deleteClass = "btn-default";

        this.mode = "js";
        this.acls = [];

        this.notifyService.startProgress();
        var promise1 = this.accountService.getRealAclState().then((result) => {
            this.useRealAcl = result.state;
            // don't hook this up until we've retrieved the initial state
            $scope.$watch(() => this.useRealAcl, (newValue, oldValue) => {
                if (newValue != oldValue) {
                    this.notifyService.startProgress();
                    this.accountService.setRealAclState(newValue)
                        .then(() => {
                            this.notifyService.notifySuccess("Set");
                        })
                        .finally(() => {
                            this.notifyService.endProgress();
                        });
                }
            });
        });
        var promise2 = this.aclService.loadAcls().then((acls) => {
            this.acls = acls;
        });

        this.$q.all([promise1, promise2])
            .finally(() => {
                this.notifyService.endProgress();
            });

        $scope.$watchCollection(() => [this.permissionPath, this.permissionType, this.read, this.write, this.create, this.del, this.mode],
            (newValue) => {
                var permissionValue = 0;

                if (this.read) {
                    permissionValue |= Consts.AccessRight.Read;
                }
                if (this.write) {
                    permissionValue |= Consts.AccessRight.Write;
                }
                if (this.create) {
                    permissionValue |= Consts.AccessRight.Create;
                }
                if (this.del) {
                    permissionValue |= Consts.AccessRight.Delete;
                }

                this.uri = URI.build({
                    protocol: Consts.Acl.AclProtocol,
                    hostname: this.permissionType,
                    path: this.permissionPath,
                    fragment: "",
                    password: "",
                    port: "",
                    username: "",
                    query: Consts.Acl.ValueParam + "=" + permissionValue.toString()
                });

                var getResult = (permissionResult: number, rightFlag: number) => {
                    if (rightFlag == 0) {
                        return 0;
                    } else if ((permissionResult & (rightFlag << 16)) == (rightFlag << 16)) {
                        return -1;
                    } else if ((permissionResult & rightFlag) == rightFlag) {
                        return 1;
                    } else {
                        return 0;
                    }
                };

                var testAcl = (uri: string): ng.IPromise<number> => {
                    if (this.mode == "api") {
                        return this.securityService.testAclPermission(uri)
                            .then(result => {
                                return result.accessResult
                            });
                    } else {
                        return this.$q.when(Utils.AclUtil.getEffectivePermissionValue(this.acls, uri));
                    }
                };

                this.notifyService.startProgress();

                var promise2 = testAcl(this.uri)
                    .then((result) => {
                        this.permissionResult = result;

                        this.accessResult = getResult(this.permissionResult, permissionValue);

                        var read = getResult(this.permissionResult, Consts.AccessRight.Read);
                        var write = getResult(this.permissionResult, Consts.AccessRight.Write);
                        var create = getResult(this.permissionResult, Consts.AccessRight.Create);
                        var del = getResult(this.permissionResult, Consts.AccessRight.Delete);

                        this.readClass = read > 0 ? "btn-success" : (read < 0 ? "btn-danger" : "btn-default");
                        this.writeClass = write > 0 ? "btn-success" : (write < 0 ? "btn-danger" : "btn-default");
                        this.createClass = create > 0 ? "btn-success" : (create < 0 ? "btn-danger" : "btn-default");
                        this.deleteClass = del > 0 ? "btn-success" : (del < 0 ? "btn-danger" : "btn-default");
                    });

                this.$q.all([promise2])
                    .finally(() => {
                        this.notifyService.endProgress();
                    });
            });


    }
}

export class TitleCoverageController {

    static $inject = ["$scope", "$q", "notifyService", "reportService", "rightsFilterService"];

    filterModel: Models.IRightsFilterModel;

    windows: Models.IPropertyCoverageWindow[];
    filterWindows: Models.IPropertyCoverageWindowMoment[];
    normalizedWindows: Models.IPropertyCoverageWindow[];
    rowCount: number;

    constructor(private $scope: ng.IScope, private $q: ng.IQService, private notifyService: NotifyService, private reportService: ReportService, private rightsFilterService: RightsFilterService) {

        var startMoment = moment().subtract(moment.duration({ "months": 1 }));
        var endMoment = startMoment.clone().add(moment.duration({ "years": 1 }));
        this.rowCount = 0;
        //this.filterModel = {
        //    startDate: "11/06/2014",
        //    endDate: "11/06/2016",
        //    selectedTerritoryIDs: [113],
        //    selectedMediaIDs: [14],
        //    selectedChannelIDs: [],
        //    selectedLanguageIDs: [],
        //    exclusive: "",
        //    assetCharID: null,
        //    selectedAssetCharValueIDs: []
        //};

        this.filterModel = this.rightsFilterService.GetCachedFilter({
            startDate: startMoment.format("L"),
            endDate: endMoment.format("L"),
            selectedTerritoryIDs: [],
            selectedMediaIDs: [],
            selectedChannelIDs: [],
            selectedLanguageIDs: [],
            exclusive: "1",
            assetCharID: null,
            selectedAssetCharValueIDs: [],
            dimFormat: { "1": true, "2": true, "3": true, "4": true }
        });
        this.windows = [];
        this.filterWindows = [];
        this.normalizedWindows = [];

        $scope.$watch(() => this.filterModel.assetCharID,
            (newValue, oldValue) => {
                if (newValue != null && oldValue != null && newValue != oldValue) {
                    if (this.windows.length > 0) {
                        this.Search();
                    }
                }
            });

        $scope.$watchCollection(() => this.filterModel.selectedAssetCharValueIDs,
            (newValue, oldValue) => {
                if (!_.isEmpty(this.windows)) {
                    this.FilterWindows();
                }
            });

        //this.Search();
    }

    Search() {
        var startDate = this.filterModel.startDate;
        var endDate = this.filterModel.endDate;
        var charID = this.filterModel.assetCharID;
        var territoryIDs = this.filterModel.selectedTerritoryIDs;
        var mediaIDs = this.filterModel.selectedMediaIDs;
        var languageIDs = this.filterModel.selectedLanguageIDs;
        var channelIDs = this.filterModel.selectedChannelIDs;

        this.notifyService.startProgress();
        this.reportService.getPropertyCoverage(startDate, endDate, territoryIDs, mediaIDs, languageIDs, channelIDs, charID)
            .then((result) => {
                this.windows = result;
                this.FilterWindows();
            })
            .finally(() => {
                this.notifyService.endProgress();
            });
    }

    FilterWindows() {
        this.filterWindows = this.windows.map((win) => {
            var windowCounts = win.windowCounts;

            windowCounts = windowCounts.filter((winCount) => _.contains(this.filterModel.selectedAssetCharValueIDs, winCount.valueID));

            return {
                startDate: moment(win.startDate).utc(),
                endDate: moment(win.endDate).utc(),
                windowCounts: _.sortBy(windowCounts, (windowCount) => windowCount.valueID)
            };
        });
        this.AnalyzeWindows();
    }

    AnalyzeWindows() {
        var resolution: moment.unitOfTime.StartOf = "month";

        var durationObj = {};
        durationObj[resolution] = 1;
        var incrDuration = moment.duration(durationObj);

        var startMoment = moment(this.filterModel.startDate).utc();
        var endMoment = moment(this.filterModel.endDate).utc();
        var indexMoment = startMoment.clone().startOf(resolution);

        this.normalizedWindows = [];

        while (indexMoment.isBefore(endMoment)) {

            var endIndexMoment = indexMoment.clone().endOf(resolution);

            var normalizedWindow: Models.IPropertyCoverageWindow = {
                startDate: indexMoment.toISOString(),
                endDate: endIndexMoment.toISOString(),
                windowCounts: []
            };

            var matchedWindows = this.filterWindows.filter((win) => indexMoment.isBefore(win.endDate) && win.startDate.isBefore(endIndexMoment) || (indexMoment.isSame(win.startDate) || endIndexMoment.isSame(win.endDate)));

            var allCounts = _.flatten<Models.IPropertyCoverageWindowCount>(matchedWindows.map((win) => win.windowCounts));
            var valueIDs = _(allCounts).map((count) => count.valueID).uniq().sortBy().value();

            valueIDs.forEach((id) => {
                var counts = allCounts.filter((c) => c.valueID == id);
                if (counts.length > 0) {
                    normalizedWindow.windowCounts.push({
                        valueID: id,
                        value: counts[0].value,
                        count: d3.mean(counts.map(c => c.count))
                    });
                }
            });

            this.normalizedWindows.push(normalizedWindow);

            indexMoment.add(incrDuration);
        }
    }

}

export class BaseEntityAssociationController {

    // progress
    jobTitle: string;
    progressJobID: string;
    public progressModalOpen: boolean;
    progressStatus: string;
    progressComplete: number;
    progressFailed: number;
    progressFailedPercentage: number;
    progressTotal: number;
    progressPercentage: number;
    progressIntervalPromise: ng.IPromise<any>;
    progressOnComplete: () => any;
    selectedEntity: any;
    visibility: number;
    baseEntityID: string;
    locked: boolean;

    constructor(private $baseScope: ng.IScope,
        public notifyService: NotifyService,
        public adminService: AdminService,
        public $interval: ng.IIntervalService,
        public $q: ng.IQService,
        private baseEntityService: EntityService,
        private baseEventService: EventService
    ) {

        this.progressJobID = "";
        this.progressModalOpen = false;
        this.progressStatus = "";
        this.progressComplete = 0;
        this.progressFailed = 0;
        this.progressTotal = 0;
        this.progressPercentage = 0;
        this.progressFailedPercentage = 0;
        this.progressIntervalPromise = null;
        this.progressOnComplete = null;
        this.visibility = 1;
        this.baseEntityID = "";

        this.$baseScope.$watch(() => {
            if (this.baseEntityID == null) {
                return null;
            } else {
                return this.baseEntityID;
            }
        },
            (newValue, oldValue) => {
                if (newValue != null) {
                    this.baseEventService.on(Consts.Events.WorkflowExecuted + newValue, () => {
                        this.UpdateRecordStatus();
                    });
                }
            });
    }

    ShowProgress(jobID: string, onComplete: () => any) {
        this.ResetProgress();

        this.progressJobID = jobID;
        this.progressOnComplete = onComplete;

        this.notifyService.startProgress();
        this.progressIntervalPromise = this.$interval(() => this.UpdateProgress(), 2000);
        this.UpdateProgress();
        this.progressModalOpen = true;
    }

    UpdateProgress() {
        this.adminService.getJobState(this.progressJobID)
            .then((result) => {
                this.progressComplete = result.completed;
                this.progressFailed = result.failed;
                this.progressStatus = result.status;
                this.progressTotal = result.total;
                this.progressPercentage = (this.progressComplete / this.progressTotal) * 100;
                this.progressFailedPercentage = (this.progressFailed / this.progressTotal) * 100;


                if (result.status == "Done" || result.status == "Failed" || result.status == "Cancelled") {
                    this.$interval.cancel(this.progressIntervalPromise);
                    if (this.progressOnComplete != null) {
                        this.$q.when(this.progressOnComplete())
                            .then(() => {
                                this.notifyService.endProgress();
                            });
                    } else {
                        this.notifyService.endProgress();
                    }
                }
            });
    }

    CancelJob() {
        this.adminService.cancelJob(this.progressJobID)
            .then((res) => {
            });
    }

    ResetProgress() {
        this.progressJobID = "";
        this.progressModalOpen = false;
        this.progressStatus = "";
        this.progressComplete = 0;
        this.progressTotal = 0;
        this.progressPercentage = 0;
        if (this.progressIntervalPromise != null) {
            this.$interval.cancel(this.progressIntervalPromise);
            this.progressIntervalPromise = null;
        }
    }

    UpdateRecordStatus() {
        this.baseEntityService.getEntity(this.baseEntityID)
            .then((result) => {
                if (result.charDataCollection != null) {
                    if (result.charDataCollection.status.lockIndicator == 0) {
                        this.locked = false;
                    }
                    else {
                        this.locked = true;
                    }
                }
            });
    }
}

export class StyleTest2Controller {

    static $inject = [];

    outerCSSClass: string;
    outerCSSStyle: string;
    leftCSSStyle: string;
    leftCSSClass: string;
    rightCSSStyle: string;
    rightCSSClass: string;
    rightVisible: boolean;

    constructor() {
        this.rightVisible = true;
        this.outerCSSClass = "flex";
        this.outerCSSStyle = "background-color:#99FF66; padding:10px;";
        this.leftCSSStyle = "background-color:#66CCFF; padding:10px;";
        this.leftCSSClass = "flex-grow";
        this.rightCSSClass = "flex-shrink";
        this.rightCSSStyle = "min-width:30%; background-color:#FF9999; padding:10px;";
    }
}
